// 編集履歴の action_type を子ども向けの文言＋アイコンに変換する表示専用ヘルパー。
// 文言は i18n（MSG_BUILDER_HISTORY_ACTION_* / MSG_BUILDER_HISTORY_EVENT_*）に定義。未知の action_type は raw 文字列でフォールバック。
// 記録と undo / redo の決まりは docs/edit-history-undo.md
import I18n from "common/i18n";
import { PieceMilestone, PieceEditHistoryEntry } from "features/builder/types";

// action_type → { i18n キー, material-symbols アイコン名 }
const HISTORY_ACTION_MAP: Record<string, { labelKey: string; icon: string }> = {
  place_asset: { labelKey: "MSG_BUILDER_HISTORY_ACTION_PLACE_ASSET", icon: "add" },
  remove_asset: { labelKey: "MSG_BUILDER_HISTORY_ACTION_REMOVE_ASSET", icon: "delete" },
  change_asset: { labelKey: "MSG_BUILDER_HISTORY_ACTION_CHANGE_ASSET", icon: "edit" },
  resize_map: { labelKey: "MSG_BUILDER_HISTORY_ACTION_RESIZE_MAP", icon: "aspect_ratio" },
  set_goal: { labelKey: "MSG_BUILDER_HISTORY_ACTION_SET_GOAL", icon: "flag" },
  set_character: { labelKey: "MSG_BUILDER_HISTORY_ACTION_SET_CHARACTER", icon: "person" },
  ai_generate: { labelKey: "MSG_BUILDER_HISTORY_ACTION_AI_GENERATE", icon: "auto_awesome" },
  rename: { labelKey: "MSG_BUILDER_HISTORY_ACTION_RENAME", icon: "edit" },
  restore: { labelKey: "MSG_BUILDER_HISTORY_ACTION_RESTORE", icon: "history" },
};

const actionLabel = (actionType: string): string => {
  const entry = HISTORY_ACTION_MAP[actionType];
  return entry ? I18n.t(entry.labelKey) : actionType;
};

// 履歴 1 行に表示するラベルとアイコンを返す。未知の type は type 文字列をそのまま表示。
// undo / redo は、何を取り消した・やり直したかを添える（「もとにもどした：ブロックを おいた」）
export const resolveHistoryLabel = (
  entry: PieceEditHistoryEntry,
): { label: string; icon: string } => {
  if (entry.action_type === "undo" || entry.action_type === "redo") {
    const undone = entry.payload?.undone;
    const isUndo = entry.action_type === "undo";
    return {
      label:
        typeof undone === "string"
          ? I18n.t(
              isUndo ? "MSG_BUILDER_HISTORY_ACTION_UNDO_OF" : "MSG_BUILDER_HISTORY_ACTION_REDO_OF",
              {
                action: actionLabel(undone),
              },
            )
          : I18n.t(isUndo ? "MSG_BUILDER_HISTORY_ACTION_UNDO" : "MSG_BUILDER_HISTORY_ACTION_REDO"),
      icon: isUndo ? "undo" : "redo",
    };
  }
  const mapped = HISTORY_ACTION_MAP[entry.action_type];
  if (!mapped) return { label: entry.action_type, icon: "history" };
  return { label: I18n.t(mapped.labelKey), icon: mapped.icon };
};

// 区切り（開いた・閉じた・完成・公開・非公開・restore・すてた）。リスト内でチャットの切り線として描画する。
// pillClass = 切り線中央ピルの背景色（Tailwind JIT が拾えるようリテラルで保持）。
const MILESTONE_MAP: Record<string, { labelKey: string; icon: string; pillClass: string }> = {
  [PieceMilestone.OPEN]: {
    labelKey: "MSG_BUILDER_HISTORY_EVENT_OPEN",
    icon: "edit_document",
    pillClass: "bg-blue-100",
  },
  [PieceMilestone.CLOSE]: {
    labelKey: "MSG_BUILDER_HISTORY_EVENT_CLOSE",
    icon: "logout",
    pillClass: "bg-gray2-100",
  },
  [PieceMilestone.COMPLETE]: {
    labelKey: "MSG_BUILDER_HISTORY_EVENT_COMPLETE",
    icon: "check_circle",
    pillClass: "bg-green-100",
  },
  [PieceMilestone.PUBLISH]: {
    labelKey: "MSG_BUILDER_HISTORY_EVENT_PUBLISH",
    icon: "public",
    pillClass: "bg-blue-100",
  },
  [PieceMilestone.UNPUBLISH]: {
    labelKey: "MSG_BUILDER_HISTORY_EVENT_UNPUBLISH",
    icon: "lock",
    pillClass: "bg-gray2-100",
  },
  [PieceMilestone.RESTORE]: {
    labelKey: "MSG_BUILDER_HISTORY_ACTION_RESTORE",
    icon: "history",
    pillClass: "bg-orange-100",
  },
  [PieceMilestone.DISCARD]: {
    labelKey: "MSG_BUILDER_HISTORY_EVENT_DISCARD",
    icon: "delete_history",
    pillClass: "bg-gray2-100",
  },
};

// 切り線として描画すべき区切りか判定。
export const isMilestoneAction = (actionType: string): boolean => actionType in MILESTONE_MAP;

// 切り線に出すラベル・アイコン・ピル色を返す。complete は「かんせい（V{n}）」のように version を添える。
export const resolveMilestone = (
  entry: PieceEditHistoryEntry,
): { label: string; icon: string; pillClass: string } => {
  const m = MILESTONE_MAP[entry.action_type];
  const base = I18n.t(m.labelKey);
  const label =
    entry.action_type === PieceMilestone.COMPLETE ? `${base}（V${entry.version}）` : base;
  return { label, icon: m.icon, pillClass: m.pillClass };
};
